import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, Badge, Button, Card, CardBody, Modal, ModalBody, ModalHeader,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { useMtffWebhookLog } from './useMtff';

/**
 * MTFF-Webhook-Receiver-Log.
 *
 * Spec: MTFF/docs/ORBIT-INTEGRATION.md §6.
 *
 * Orbit's `frontend/server.js` verifiziert HMAC-SHA256 mit dem in
 * `MTFF_WEBHOOK_SECRET` hinterlegten Schlüssel, dropt invalide Signaturen
 * mit 401 und legt valide Events in einem In-Memory-Ringbuffer
 * (`/webhooks/mtff/log`). Diese View pollt den Buffer alle 15s.
 *
 * Wenn Orbit horizontal skaliert wird, muss der Buffer in Redis wandern —
 * im aktuellen Single-Instance-Deploy reicht der Speicher.
 */
const TYPE_COLOR = {
  'theme.published': 'success',
  'instance.degraded': 'warning',
  'wordpress.revalidate.failed': 'danger',
};

export function MtffWebhooksPage() {
  const query = useMtffWebhookLog();
  const [openItem, setOpenItem] = useState(null);
  const items = query.data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="MTFF · Webhooks"
        subtitle={`${items.length} Events im Buffer · Auto-Poll 15s · HMAC-verifiziert`}
        actions={
          <Button color="secondary" outline size="sm" tag={Link} to="/admin/settings/mtff/instances">
            ← Instances
          </Button>
        }
      />

      <Alert color="info" className="small">
        Receiver-Endpoint: <code>POST /webhooks/mtff</code> mit
        {' '}<code>X-MTFF-Signature: t=&lt;unix&gt;,v1=&lt;hex&gt;</code> über
        {' '}<code>&lt;t&gt;.&lt;rawBody&gt;</code>. Skew-Toleranz 5 min,
        Constant-Time-Compare. Invalide Signaturen werden mit 401
        verworfen und NICHT geloggt.
      </Alert>

      <Card><CardBody>
        <DataTable
          loading={query.isLoading}
          error={query.error}
          empty={!query.isLoading && items.length === 0}
          emptyMessage="Noch keine Events empfangen."
          onRetry={() => query.refetch()}
        >
          <thead>
            <tr>
              <th style={{ width: 230 }}>Type</th>
              <th style={{ width: 130 }}>Instance</th>
              <th style={{ width: 160 }}>Empfangen</th>
              <th style={{ width: 160 }}>Sig-Zeitstempel</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((e, idx) => (
              <tr key={`${e.receivedAt}-${idx}`}
                role="button"
                onClick={() => setOpenItem(e)}
                style={{ cursor: 'pointer' }}>
                <td>
                  <Badge color={TYPE_COLOR[e.type] ?? 'secondary'} pill className="orbit-code-badge-sm">
                    {e.type}
                  </Badge>
                </td>
                <td className="small">{e.instanceId ?? '—'}</td>
                <td>{e.receivedAt ? <RelativeTime iso={e.receivedAt} /> : '—'}</td>
                <td>{e.signatureTimestamp ? <RelativeTime iso={e.signatureTimestamp} /> : '—'}</td>
                <td className="text-end small text-muted">Payload öffnen →</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      </CardBody></Card>

      <Modal isOpen={!!openItem} toggle={() => setOpenItem(null)} size="lg">
        <ModalHeader toggle={() => setOpenItem(null)}>
          {openItem?.type} · {openItem?.receivedAt
            ? new Date(openItem.receivedAt).toLocaleString('de-DE') : ''}
        </ModalHeader>
        <ModalBody>
          <pre
            className="bg-body-tertiary p-2 small font-monospace"
            style={{ borderRadius: 6, maxHeight: 480, overflow: 'auto' }}
          >
            {JSON.stringify(openItem?.payload ?? {}, null, 2)}
          </pre>
        </ModalBody>
      </Modal>
    </div>
  );
}
